/**
 * Product Bridge List Component
 * 
 * Simple component that displays the list of products for the Product Domain Bridge.
 * Used in the default Control Bridge view (not the full detail section).
 * 
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md - Domain Bridge definitions
 */

import React from 'react';
import { Typography, Tag, Tooltip } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import { ValueMetricDisplay, ValueType, getEntropyInfo } from '../Shared/ValueMetricDisplay';
import styles from './ProductBridgeList.module.css';

const { Title, Text } = Typography;

export interface Product {
  id: string;
  name: string;
  valueRatio: number;
  entropyScore: number;
  status: 'idea' | 'development' | 'launch' | 'revenue';
}

export interface ProductBridgeListProps {
  /** Bridge ID */
  bridgeId: string;
  /** Bridge name */
  bridgeName: string;
  /** Bridge icon */
  icon: React.ReactNode;
  /** Bridge color */
  color: string;
  /** Overall value ratio for the bridge */
  valueRatio: number;
  /** Overall entropy score */
  entropyScore: number;
  /** Value type for ratio thresholds */
  valueType: ValueType;
  /** Products in this bridge */
  products: Product[];
  /** Whether this bridge is selected for full-width view */
  isSelected?: boolean;
  /** Callback when bridge is clicked */
  onClick?: () => void;
  /** Callback when a product is clicked */
  onProductClick?: (productId: string, productName: string) => void;
}

const getProductStatusLabel = (status: Product['status']): string => {
  switch (status) {
    case 'idea': return 'Idea';
    case 'development': return 'Development';
    case 'launch': return 'Launch';
    case 'revenue': return 'Revenue';
    default: return 'Unknown';
  }
};

const getProductStatusColor = (status: Product['status']): string => {
  switch (status) {
    case 'idea': return '#8b5cf6';
    case 'development': return '#3b82f6';
    case 'launch': return '#f59e0b';
    case 'revenue': return '#10b981';
    default: return '#6b7280';
  }
};

export const ProductBridgeList: React.FC<ProductBridgeListProps> = ({
  bridgeId,
  bridgeName,
  icon,
  color,
  valueRatio,
  entropyScore,
  valueType,
  products,
  isSelected = false,
  onClick,
  onProductClick,
}) => {
  const entropyInfo = getEntropyInfo(entropyScore);
  const isClickable = !!onClick;

  return (
    <div
      className={`${styles.container} ${isSelected ? styles.selected : ''} ${isClickable ? styles.clickable : ''}`}
      style={{ '--bridge-color': color } as React.CSSProperties}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && onClick?.() : undefined}
    >
      {/* Bridge Header */}
      <div className={styles.header}>
        <div className={styles.iconContainer} style={{ backgroundColor: `${color}20`, color }}>
          {icon}
        </div>
        <Title level={4} className={styles.title}>
          {bridgeName}
        </Title>
        {isSelected && <span className={styles.selectedBadge}>Active</span>}
      </div>

      {/* Metrics */}
      <div className={styles.metrics}>
        <ValueMetricDisplay
          valueRatio={valueRatio}
          entropyScore={entropyScore}
          valueType={valueType}
          variant="compact"
          showLabels={false}
        />
      </div>

      {/* Products List */}
      <div className={styles.productsSection}>
        <Text className={styles.productsLabel}>Products</Text>
        {products.length > 0 ? (
          <div className={styles.productsList}>
            {products.map((product) => {
              const entropyInfo = getEntropyInfo(product.entropyScore);
              return (
                <Tooltip
                  key={product.id}
                  title={
                    <div>
                      <div><strong>{product.name}</strong></div>
                      <div>Value Ratio: {product.valueRatio.toFixed(2)}</div>
                      <div>Entropy: {product.entropyScore} ({entropyInfo.level})</div>
                      <div>Status: {getProductStatusLabel(product.status)}</div>
                      {onProductClick && <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.8 }}>Click to view product</div>}
                    </div>
                  }
                >
                  <div 
                    className={`${styles.productItem} ${onProductClick ? styles.clickable : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onProductClick?.(product.id, product.name);
                    }}
                    role={onProductClick ? 'button' : undefined}
                    tabIndex={onProductClick ? 0 : undefined}
                    onKeyDown={onProductClick ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        onProductClick(product.id, product.name);
                      }
                    } : undefined}
                  >
                    <Tag
                      color={getProductStatusColor(product.status)}
                      className={styles.productTag}
                    >
                      {product.name}
                    </Tag>
                    <div className={styles.productMetrics}>
                      <span className={styles.productRatio}>
                        Ratio: {product.valueRatio.toFixed(2)}
                      </span>
                      <span
                        className={styles.productEntropy}
                        style={{ color: entropyInfo.color }}
                      >
                        Entropy: {product.entropyScore}
                      </span>
                    </div>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        ) : (
          <Text type="secondary" className={styles.noProducts}>
            No products available
          </Text>
        )}
      </div>
    </div>
  );
};

export default ProductBridgeList;
